import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MoveRight } from "lucide-react";
import { LineReveal } from "@/components/Reveal";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] pt-28 pb-20 lg:pt-36 lg:pb-32 overflow-hidden noise"
      data-testid="hero-section"
    >
      {/* Grid backdrop */}
      <div className="absolute inset-0 grid-bg opacity-70" />
      {/* Orbs */}
      <motion.div style={{ y: y1, scale }} className="hero-orb w-[720px] h-[720px] -top-40 -left-40 glow-primary" />
      <motion.div style={{ y: y2 }} className="hero-orb w-[520px] h-[520px] top-40 -right-40 glow-accent" />
      <motion.div style={{ y: y1 }} className="hero-orb w-[420px] h-[420px] bottom-20 left-1/3 glow-secondary" />

      {/* 3D-ish glass sphere */}
      <motion.div
        style={{ y: y2, opacity }}
        className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full"
      >
        <div className="absolute inset-0 rounded-full" style={{
          background: "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.5), rgba(37,99,235,0.4) 30%, rgba(124,58,237,0.6) 60%, rgba(9,9,11,0.9) 100%)",
          boxShadow: "inset 30px 30px 120px rgba(6,182,212,0.3), inset -30px -30px 120px rgba(124,58,237,0.4), 0 30px 80px rgba(37,99,235,0.4)",
        }} />
        <div className="absolute inset-0 rounded-full mix-blend-overlay"
             style={{ background: "radial-gradient(circle at 70% 80%, rgba(6,182,212,0.6), transparent 40%)" }} />
        {/* Rings */}
        <div className="absolute inset-0 rounded-full border border-white/10 rotate-[20deg]"
             style={{ transform: "rotate(20deg) scale(1.25)" }} />
        <div className="absolute inset-0 rounded-full border border-white/5 -rotate-[15deg]"
             style={{ transform: "rotate(-15deg) scale(1.4)" }} />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2 mb-8"
        >
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
          </span>
          <span className="overline">Now booking Q1 · London → Global</span>
        </motion.div>

        <h1 className="font-serif tracking-[-0.02em] text-[clamp(3rem,8vw,7.5rem)] leading-[0.95]" data-testid="hero-headline">
          <LineReveal delay={0.05}>Building</LineReveal>{" "}
          <LineReveal delay={0.15}>digital</LineReveal>{" "}
          <LineReveal delay={0.25}>
            <span className="italic text-gradient">businesses</span>
          </LineReveal>
          <br />
          <LineReveal delay={0.4}>for the</LineReveal>{" "}
          <LineReveal delay={0.5}>
            <span className="italic">modern</span>
          </LineReveal>{" "}
          <LineReveal delay={0.6}>world.</LineReveal>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-[560px] text-[15.5px] md:text-base leading-relaxed text-[color:var(--text-muted)]"
        >
          From business websites and e-commerce platforms to custom software, automation
          and enterprise solutions — LaneX Labs helps ambitious businesses build, launch
          and scale with confidence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-3"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-quote"
            className="group h-12 pl-6 pr-4 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] font-semibold text-[14px] inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            Request a Quote
            <span className="w-7 h-7 rounded-full bg-[color:var(--bg)] text-[color:var(--text)] grid place-items-center transition-transform group-hover:translate-x-0.5">
              <ArrowUpRight size={14} />
            </span>
          </Link>
          <Link
            to="/work"
            data-testid="hero-cta-work"
            className="group h-12 px-6 rounded-full border border-[color:var(--border-strong)] font-semibold text-[14px] inline-flex items-center gap-2 hover:bg-[color:var(--surface-elev)] transition-colors"
          >
            Explore Our Work
            <MoveRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        {/* Bottom meta strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-20 lg:mt-32 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-[color:var(--border)] pt-8"
        >
          {[
            ["180+", "Projects delivered"],
            ["98%", "Customer satisfaction"],
            ["4 wks", "Average delivery"],
            ["14+", "Industries served"],
          ].map(([v, l]) => (
            <div key={l}>
              <div className="font-serif text-3xl md:text-4xl tracking-tight">{v}</div>
              <div className="mt-1 text-[12px] text-[color:var(--text-muted)] font-mono tracking-wider uppercase">{l}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2"
      >
        <div className="w-px h-10 bg-gradient-to-b from-transparent via-[color:var(--text-muted)] to-transparent" />
        <span className="font-mono text-[10px] tracking-[0.2em] text-[color:var(--text-muted)]">SCROLL</span>
      </motion.div>
    </section>
  );
}
